import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useReducer, useState } from "react";

import { ToastContainer } from "react-toastify";
import {
  getPostById,
  updatePost,
  clearMessage,
} from "@/services/blogServices/blogActions";
import { useCurrentSite } from "@/services/siteServices/useCurrentSite";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";
import { useBlockImageUpload } from "@/services/ImageServices/useBlockImageUpload";
import NotificationAlertOptions from "@/layouts/Alerts";
import UpdatePostView from "./UpdatePostView";
import { useParams } from "react-router-dom";
import { altOf } from "@/views/pages/components/Media/ModelMediaContent/ImageInformation";
import { asCaught } from "@/services/caughtError";
import { postForm, userIdOf } from "./postForm";

const formActions = postForm.actions;

const encodeHtml = (s = "") =>
  s.split("<").join("&lt;").split(">").join("&gt;");

export default function UpdatePostContainer() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const uploadImage = useImageUpload();
  const uploadBlockImage = useBlockImageUpload();

  const postById = useAppSelector((s) => s.blog.post_byid) || {};
  const blog = useAppSelector((s) => s.blog);
  const currentsite = useCurrentSite();

  // SCRUM-2261: the post draft is the shared postForm createSlice, mounted locally. The update
  // screen starts with no block rows until the post arrives (the create screen starts with one).
  const [form, dispatchForm] = useReducer(postForm.reducer, {
    ...postForm.getInitialState(),
    blocks: [],
  });
  const { blocks, reactQuillText, picture, title, type, editor, reviewer } = form;
  const post: Record<string, unknown> = form.data || {};
  const countryCode = currentsite?.country_code || "";
  // What the post read returned, for the block defaults and the byline comparison (SCRUM-1918).
  const initialBlocks: any[] = Array.isArray(postById.blocks) ? postById.blocks : []; // any: blog.post_byid is typed any (blogSlice.ts:33)
  const loadedByline = {
    editor: userIdOf(postById.editor),
    reviewer: userIdOf(postById.reviewer),
  };

  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState({ title: true, content: true });

  // Fetch the post data when the component mounts
  useEffect(() => {
    if (id) dispatch(getPostById(id));
  }, [dispatch, id]);

  // Fill the form when post data is loaded — also when the route moves to another post without
  // an unmount (AdminContainer keys the route by its pattern).
  useEffect(() => {
    if (postById && Object.keys(postById).length > 0) {
      dispatchForm(formActions.postLoaded(postById));
    }
  }, [postById]);

  // Show success message after successful update
  useEffect(() => {
    if (blog?.message) {
      NotificationAlertOptions("success", blog.message);
      dispatch(clearMessage());
      setLoading(false);
    }
  }, [blog?.message, dispatch]);

  //  Form Handlers
  const handleQuillChange = (val) => dispatchForm(formActions.contentChanged(val));

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "title") {
      dispatchForm(formActions.titleChanged(value));
      setValidation((v) => ({ ...v, title: true }));
    }
  };

  const handleToggle = (e) => dispatchForm(formActions.typeChanged(e.target.checked));

  // Handle main image selection
  const handleImageUpload = (imgObj) => dispatchForm(formActions.pictureReceived(imgObj));

  const handleAddBlocks = (arr) => dispatchForm(formActions.blocksSet(arr));
  const handleDeleteBlocks = (arr) => dispatchForm(formActions.blocksSet(arr));

  // Merge incoming blocks with their initial values (keep defaults)
  const normBlocksWithDefaults = (arr) =>
    arr.map((block, index) => {
      const init = initialBlocks[index] || {};
      return {
        ...block,
        index,
        title:
          block.title !== undefined ? block.title : (init.title ?? "") || "",
        description:
          block.description !== undefined
            ? block.description
            : (init.description ?? "") || "",
        image:
          block.image !== undefined ? block.image : (init.image ?? "") || "",
      };
    });

  // Process block updates and upload new images if needed
  const handleBlockArray = async (incoming) => {
    if (!incoming) return;
    const merged = normBlocksWithDefaults(incoming);
    const prev = blocks;
    const cc = (countryCode || "").toLowerCase();

    const processed = await Promise.all(
      merged.map(async (block) => {
        if (block?.image?.data) {
          if (prev[block.index]?.image !== block.image) {
            const path = `/${cc}/POSTS/${block.image.imgName}`;
            try {
              // Linearised (SCRUM-1021): uploadImagesAction resolves to the
              // uploaded URL, so set it on the block inline instead of the old
              // uploadedImages watch-effect that merged it back by index.
              const url = await uploadBlockImage(
                block.index,
                block.image.data,
                path
              );
              return { ...block, image: url };
            } catch {
              NotificationAlertOptions(
                "error",
                `Error uploading image in block ${block.index + 1}`
              );
              return { ...block };
            }
          }
          return { ...block };
        }

        if (block?.image?.secure_url) {
          return { ...block, image: block.image.secure_url };
        }

        return { ...block, image: block?.image || "" };
      })
    );

    dispatchForm(formActions.blocksSet(processed));
  };

  // A refused save (SCRUM-2374): the notification hub shows the server's message
  // and clears state.errors; this screen only marks the fields the server
  // reported empty and stops the spinner.
  const savePost = async (body: Record<string, unknown>) => {
    try {
      await dispatch(updatePost(body));
    } catch (err) {
      const serverErrors = asCaught(err)?.response?.data?.errors;
      if (Array.isArray(serverErrors)) {
        setValidation((prev) => {
          const next = { ...prev };
          serverErrors.forEach((e) => {
            if (e.value === "" && e.param) next[e.param] = false;
          });
          return next;
        });
      }
      setLoading(false);
    }
  };

  // Handle Update button click
  const onClickUpdate = async () => {
    const nextValidation = {
      title: !!title?.trim(),
      content: !!reactQuillText?.trim(),
    };
    setValidation(nextValidation);
    if (!nextValidation.title || !nextValidation.content) return;

    const encoded = encodeHtml(reactQuillText);
    const cc = (countryCode || "").toLowerCase();

    // SCRUM-1918: the stored editor/reviewer go back only when the user changed
    // them (a cleared one as null). Sent back unchanged, a soft-deleted user's id
    // would make every save of this post a 400 (SCRUM-1917 accepts live users only).
    const loaded = { ...post };
    delete loaded.editor;
    delete loaded.reviewer;
    let updatedPost: Record<string, unknown> = {
      ...loaded,
      ...(editor !== loadedByline.editor ? { editor } : {}),
      ...(reviewer !== loadedByline.reviewer ? { reviewer } : {}),
      type,
      title,
      content: encoded,
      pathpost: `blog/${title}`,
      // No `pathauthor` here (SCRUM-1064): it is the author-page URL and belongs
      // to the post's author, not to whoever is editing. The spread above keeps
      // the loaded post's own value; the server ignores it on update anyway.
      blocks,
    };
    dispatchForm(formActions.postSaved(updatedPost));

    // Upload new main image if selected
    if (picture?.data) {
      const path = `/${cc}/POSTS/${picture.imgName}`;
      setLoading(true);
      // Linearised (SCRUM-1021): upload via useImageUpload, then save inline with
      // the returned URL — no more uploadUrl watch-effect (the old success-only
      // effect spun forever with no toast on a failed upload). A failed upload
      // re-throws a plain Error (SCRUM-815); surface it on this view's own
      // ToastContainer and clear the spinner.
      let url;
      try {
        url = await uploadImage(picture.data, path);
      } catch (err) {
        setLoading(false);
        NotificationAlertOptions(
          "error",
          asCaught(err)?.message || "Uploading the cover image failed. Please try again."
        );
        return;
      }
      // SCRUM-1595: a new file replaces the picture, so the old alt goes too.
      const withPic = { ...updatedPost, picture: url, picture_alt: "" };
      dispatchForm(formActions.postSaved(withPic));
      // Outside the upload try (SCRUM-2374): a refused save is not a failed upload.
      await savePost(withPic);
      return;
    }

    // If the image already has a hosted URL
    if (picture?.secure_url !== undefined) {
      // SCRUM-1595: a picture picked from the media library brings its stored
      // alt; an unchanged picture keeps the post's own picture_alt (spread above).
      updatedPost = { ...updatedPost, picture: picture.secure_url, picture_alt: altOf(picture) };
    }

    setLoading(true);
    await savePost(updatedPost);
  };

  // Generate image URL for preview
  const imageForPreview =
    picture?.secure_url || (typeof picture === "string" && picture ? picture : null);

  // Used to disable the Update button when form is invalid
  const isFormValid = !!title?.trim() && !!reactQuillText?.trim();

  return (
    <>
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <UpdatePostView
        loading={loading}
        type={type}
        title={title}
        reactQuillText={reactQuillText}
        validation={validation}
        blocks={blocks}
        onToggleType={handleToggle}
        onTitleChange={handleChange}
        onQuillChange={handleQuillChange}
        onBlocksAdd={handleAddBlocks}
        onBlocksDelete={handleDeleteBlocks}
        onBlocksProcess={handleBlockArray}
        onImageUpload={handleImageUpload}
        imagePreview={imageForPreview}
        onUpdateClick={onClickUpdate}
        editor={editor}
        reviewer={reviewer}
        onEditorChange={(value) => dispatchForm(formActions.editorChanged(value))}
        onReviewerChange={(value) => dispatchForm(formActions.reviewerChanged(value))}
      />
    </>
  );
}
